import {
  isAllUpSelection,
  isFobMatchedSelection,
  isFobPropositionSelection,
  isMysterySelection,
  isRaceDetails,
  isSameRaceMultiSelection,
  isStartingPriceSelection,
  isToteSelection,
} from '@mobi/betslip/helpers/typeGuards'
import type {
  AllUpSelection,
  BetSlipResponse,
  BetSource,
  EventDetails,
  FobMatchedSelection,
  FobPropositionSelection,
  MysteryDetails,
  MysterySelection,
  RaceDetails,
  Selection,
  ToteSelection,
} from '@mobi/betslip/types'
import { FeatureFlagKeyName } from '@mobi/settings'
import { trackBetLoopCopyBetPlaced } from '@core/Areas/BetLoop/helpers/analytics'
import { trackShareBetLinkPlaced } from '@core/Areas/ShareBet/helpers/analytics'
import {
  selectIsSkyVideoPlayerOpen,
  selectVideoPlayerChannel,
} from '@core/Areas/SkyVideoPlayer/Store/selectors'
import { isMysteryDetails, isToteSportsDetails } from '@core/Data/Betting/selectionDetails'
import { isFavouriteNumbersSelection } from '@core/Data/Betting/selections'
import { trackBettingEvent } from '@core/Services/Analytics/helpers/trackers'
import { keys } from '@core/Services/Analytics/keys'
import { FeatureFlagsState } from '@core/State/FeatureFlags/Store'
import { store } from '@core/Store'
import { defaultData } from '../../dataLayer'
import type { AnalyticsBetData, BetConstruction, MysteryBetData } from '../../dataLayer/types'

export const trackBetConfirmation = ({
  ticketNumber,
  selection,
  selectionDetails,
  specialOffers,
  betConstruction,
  betCost,
  winInvestment,
  placeInvestment,
  features,
  bonusCashAmount,
  betSource,
  betSourceShareOrigin,
}: {
  ticketNumber: number
  selection: Selection | null
  selectionDetails: EventDetails | null
  specialOffers: BetSlipResponse['specialOffers']
  betConstruction: BetConstruction
  betCost: number
  winInvestment: number
  placeInvestment: number
  features: FeatureFlagsState
  bonusCashAmount: number
  betSource?: BetSource | null
  betSourceShareOrigin?: string | null
}): void => {
  const isToteBet = isToteSelection(selection)
  const isSportsBet = isToteSportsDetails(selectionDetails)
  const isMysteryBet = isMysteryDetails(selectionDetails)
  const isFavouriteNumbersBet = isFavouriteNumbersSelection(selection)

  let meetingCode: string | null = null
  let meetingName: string | null = null
  const races = (selectionDetails as RaceDetails)?.races
  if (races && races.length) {
    meetingCode = races[0].meetingCode
    meetingName = races[0].meetingName
  }

  const sportsOrRacingData = {
    [isSportsBet ? 'sports' : 'racing']: {
      [isToteBet || isMysteryBet || isFavouriteNumbersBet ? 'tote' : 'fixed']: {
        cost:
          isToteBet || isMysteryBet || isFavouriteNumbersBet
            ? betCost
            : winInvestment + placeInvestment,
        numberOfTickets: 1,
        bonusCashUsed: bonusCashAmount > 0,
        bonusCashAmount,
      },
      info: (selection as ToteSelection | FobMatchedSelection | MysterySelection).fixtureId,
      selection: {
        ...selection, // assist GA by storing all properties of all the different selection Typescript
        betSource,
        raceCode: meetingCode,
        meetingName,
      },
    },
  }

  const betType = getBetType(selection, selectionDetails)
  const betSpecialOffers =
    specialOffers && specialOffers.length > 0 ? specialOffers.map(so => so.title) : null

  let mystery: MysteryBetData | null = null
  if (isMysteryBet) {
    const mysteryDetails = selectionDetails as MysteryDetails
    mystery = {
      isQuickPick: mysteryDetails.betType === 'mystery-quick-pick',
      mysteryType: mysteryDetails.betTypeName,
    }
  }

  const bet: AnalyticsBetData = {
    ...defaultData.bet,
    ...sportsOrRacingData,
    id: ticketNumber,
    betType,
    productType: 'single',
    placementMethod: 'account',
    errorMessage: null,
    construction: betConstruction,
    specialOffers: betSpecialOffers as string[],
    mystery,
  }

  const userFeatures = Object.keys(features)
    .filter(key => features[key as FeatureFlagKeyName] === true)
    .sort()
    .join('|')

  const currentAppState = store.getState()
  const isVisionOpen = selectIsSkyVideoPlayerOpen(currentAppState)
  const visionChannel = selectVideoPlayerChannel(currentAppState)

  trackBettingEvent(keys.betAccountProcessed, {
    user: { featureToggles: userFeatures, isVisionOpen, visionChannel },
    bet,
  })

  if (betSourceShareOrigin) {
    const raceCode = meetingCode?.toLowerCase() ?? undefined
    const amountPaid =
      isToteBet || isMysteryBet || isFavouriteNumbersBet ? betCost : winInvestment + placeInvestment
    const hasSpecialOffer = (specialOffers || []).length > 0

    if (betSource === 'bet-loop') {
      const origin = betSourceShareOrigin
      trackBetLoopCopyBetPlaced({ amountPaid, hasSpecialOffer, legs: 1, raceCode, origin })
    } else {
      const share_origin = betSourceShareOrigin
      const has_special = hasSpecialOffer
      trackShareBetLinkPlaced({ spend: amountPaid, has_special, legs: 1, raceCode, share_origin })
    }
  }
}

// =============
// Local Helpers
// =============

const getBetType = (
  selection: Selection | null,
  selectionDetails: EventDetails | null
): string | null => {
  if (isSameRaceMultiSelection(selection)) {
    return 'Same Race Multi'
  }

  if (isMysterySelection(selection)) {
    return 'Mystery'
  }

  if (isFavouriteNumbersSelection(selection)) {
    return 'Favourite Numbers'
  }

  if (isToteSelection(selection)) {
    return (selection as ToteSelection).betType.toString()
  }

  if (isFobMatchedSelection(selection)) {
    return 'Win & Place Fixed'
  }

  if (isStartingPriceSelection(selection)) {
    return 'Starting Price'
  }

  if (isAllUpSelection(selection)) {
    return (selection as AllUpSelection).betType.toString()
  }

  if (isFobPropositionSelection(selection)) {
    const marketName = (selection as FobPropositionSelection).marketName
    if (marketName) {
      return marketName
    }

    if (isRaceDetails(selectionDetails)) {
      return 'Race Fixed'
    }
  }

  return null
}
